import { useDispatch } from 'react-redux';

// material-ui
import { Button, DialogActions, DialogContent, DialogTitle, Divider, Grid, InputLabel, Stack, TextField, Tooltip } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import _ from 'lodash';
import * as Yup from 'yup';
import { Form, FormikProvider, FormikValues, useFormik } from 'formik';

// project imports
import IconButton from 'components/@extended/IconButton';
import { openSnackbar } from 'store/reducers/snackbar';

// assets
import { DeleteFilled } from '@ant-design/icons';

// constant
const getInitialValues = (entity: FormikValues | null) => {
  const newEntity = {
    name: ''
  };

  if (entity) {
    newEntity.name = entity.name;
    return _.merge({}, newEntity, entity);
  }

  return newEntity;
};

export interface Props {
  entity?: any;
  onCancel: () => void;
  onSave: (name: string) => void;
}

const AddGridPreferenceView = ({ entity, onCancel, onSave }: Props) => {
  const dispatch = useDispatch();
  const CustomerSchema = Yup.object().shape({
    name: Yup.string().required('Name for new view state is required')
  });

  const deleteHandler = async () => {
    try {
      onSave(entity);
      dispatch(
        openSnackbar({
          open: true,
          message: 'Deleted successfully.',
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
      onCancel();
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(entity!),
    validationSchema: CustomerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      onSave(values.name);
      setSubmitting(false);
      onCancel();
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle>{entity ? 'Edit Settings View' : 'New Settings View'}</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={1.25}>
                  <InputLabel htmlFor="GridPreferenceView-entity-description">Name</InputLabel>
                  <TextField
                    variant="filled"
                    fullWidth
                    id="GridPreferenceView-entity-description"
                    placeholder="Enter Name"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                {entity && (
                  <Tooltip title="Delete View" placement="top">
                    <IconButton onClick={deleteHandler} size="large" color="error">
                      <DeleteFilled />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button color="error" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    Save
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </DialogActions>
        </Form>
      </LocalizationProvider>
    </FormikProvider>
  );
};

export default AddGridPreferenceView;
